<template>
  <b-card class="rounded-lg">
    <template #header>
      <div class="d-flex justify-content-between align-items-center w-100 pb-1" style="border-bottom: 2px solid rgb(238, 238, 238);">
        <h4 class="text-dark m-0">
          Notes
        </h4>

        <div v-if="can('dietician_notes:CanAddNew') && currentUserProfession === 'dietician'" class="d-flex align-items-center">
          <b-button size="sm" variant="link" class="p-0" @click="onAddNew">
            <feather-icon
              icon="PlusCircleIcon"
              size="25"
            />
          </b-button>
        </div>
      </div>
    </template>
    

    <div class="" style="height: 480px; overflow-y: scroll;">
      <b-overlay :show="loading" opacity="0.5">
        <add-or-update-dieticians-note-modal
          :editing-data.sync="editingData"
          :is-add-active.sync="toggleModal"
          :consultation-data="consultationData"
          resource="dietician_notes"
          modal-title="Notes"
          @create="onCreate"
          @refetchData="onRefresh"
        />

        <div>
          <list-table
            :records="records" 
            :columns="tableColumns"
            :current-page-number="1"
            :show-import-button="false"
            :show-export-button="false"
            :show-search="false"
            :total-records="totalRecords"
            @row-clicked="onNote"
          >
            <template #cell(status)="data">
              <b-badge
                pill
                :variant="data.item.status === 'active' ? `light-success` : 'light-danger'"
                class="text-capitalize"
              >
                {{ getUserStatusText(data.item.status) }}
              </b-badge>
            </template>

            <template #cell(duration)="data">
              <span>
                {{ `${data.item.duration} ${startCase(data.item.duration_unit)}` }}
              </span>
            </template>

            <template #cell(actions)="data">
              <div v-if="currentUserProfession === 'dietician'">
                <b-dropdown
                  variant="link"
                  no-caret
                  :right="$store.state.appConfig.isRTL"
                >
                  <template #button-content>
                    <feather-icon
                      icon="MoreVerticalIcon"
                      size="16"
                      class="align-middle text-body"
                    />
                  </template>
                  
                  <b-dropdown-item @click="onNote(data.item)">
                    <feather-icon icon="EditIcon" />
                    <span class="align-middle ml-50">Edit</span>
                  </b-dropdown-item>
  
                  <b-dropdown-item @click="onDelete(data.item)">
                    <feather-icon icon="Trash2Icon" />
                    <span class="align-middle ml-50">Delete</span>
                  </b-dropdown-item>
                </b-dropdown>
              </div>
              <span v-else class="ml-2">-</span>
            </template>
          </list-table>
        </div>
      </b-overlay>
    </div>
  </b-card>
</template>

<script>
import {
  BCard,
  BImg,
  BRow,
  BCol,
  BBadge,
  BAlert,
  BButton,
  BAvatar,
  BOverlay,
  BDropdown,
  BDropdownItem,
  BDropdownGroup
} from 'bootstrap-vue'

import { get, startCase } from 'lodash';

import Ripple from 'vue-ripple-directive'
import ListTable from "@/@core/components/ListTable/ListTable.vue";
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import AddOrUpdateDieticiansNoteModal from '@/@core/components/shared/admin/modals/AddOrUpdateDieticiansNoteModal.vue';

export default {
  components: {
    BImg,
    BRow,
    BCol,
    BCard,
    BBadge,
    BAlert,
    BAvatar,
    BButton,
    BOverlay,
    BDropdown,
    BDropdownItem,
    BDropdownGroup,

    ListTable,
    AddOrUpdateDieticiansNoteModal
  },
  directives: {
    Ripple,
  },
  props: {
    consultationData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,

      editingData: null,
      toggleModal: false,
      
      records: [],
      tableColumns: [
        { key: 'note', sortable: false, },
        { key: 'created', label: 'Date', sortable: false, formatter: val => `${this.formatDate(val, 'MM-yy')}`, class: 'w-10' },
        { key: 'actions' },
      ],
    }
  },
  computed: {
    totalRecords: {
      get(){
        return this.$store.getters[`navigation/totalRecords`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_TOTAL_RECORDS`, value)
      }
    },
  },
  created() {
    this.fetchAllData();
  },
  methods: {
    async fetchAllData() {
      try {
        this.loading = true; 

        const query = {
          page: 1,
          limit: 100,
          consultation_id: this.consultationData._id,
        }

        const request = await this.useJwt().mainService.dieticianNotes.list(query);
        const { data, pagination } = request.data;
        this.records = data;
        this.totalRecords = pagination.totalRecords
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        console.log("error_message", error_message)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async onCreate(form_data){
      try {
        this.loading = true;

        const { payload } = form_data;
        await this.useJwt().mainService.dieticianNotes.create(payload);
        this.fetchAllData();

        this.toggleModal = false;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            variant: 'success',
            icon: 'CheckCircleIcon',
            text: `Record added successfully`
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    async onDelete(form_data){
      try {
        this.loading = true;

        await this.useJwt().mainService.dieticianNotes.delete(form_data.id);
        this.$emit('refresh')
        this.fetchAllData();

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: `Note deleted successfully`
          },
        });
      } catch (error){
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    },
    onRefresh() {
      this.$emit('refresh')
      this.fetchAllData();
    },
    onAddNew(){
      this.editingData = null;
      this.toggleModal = true;
    },
    onNote(record){
      this.editingData = record;
      this.toggleModal = true;
    },
    startCase
  }
}
</script>

<style scoped>
.profile-highlight {
  font-size: 18px !important;
}
.profile-highlight-title {
  font-size: 16px !important;
  color: lightgray;
  font-weight: normal;
}

.patient-name {
  font-size: 25px;
}
</style>

<style>
.w-10 {
  width: 13% !important;
}
</style>
